import { useState, useCallback, useEffect, useRef } from "react";

const STORAGE_KEY = "correo-column-widths";
const DEFAULTS = { sidebar: 260, inbox: 380 };
const MIN_SIDEBAR = 180;
const MIN_INBOX = 280;
const MIN_VIEWER = 320;

const loadWidths = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    const sidebar = Math.max(MIN_SIDEBAR, Math.min(500, Number(parsed.sidebar) || DEFAULTS.sidebar));
    const inbox = Math.max(MIN_INBOX, Math.min(600, Number(parsed.inbox) || DEFAULTS.inbox));
    return { sidebar, inbox };
  } catch {
    return DEFAULTS;
  }
};

const saveWidths = (widths) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widths));
  } catch (e) {
    console.warn("No se pudo guardar anchos en localStorage:", e);
  }
};

const Resizer = ({ onDragStart, position }) => (
  <div
    role="separator"
    aria-orientation="vertical"
    className={`absolute top-0 bottom-0 w-1 cursor-col-resize z-10 group
      hover:bg-orange-400/50 active:bg-orange-500/70 transition-colors
      ${position === "left" ? "left-0 -translate-x-1/2" : "right-0 translate-x-1/2"}`}
    onMouseDown={onDragStart}
    style={{ touchAction: "none" }}
  >
    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-0.5 bg-gray-300 group-hover:bg-orange-400" />
  </div>
);

const MailLayout = ({ sidebar, inbox, viewer }) => {
  const containerRef = useRef(null);
  const [widths, setWidths] = useState(loadWidths);
  const [resizing, setResizing] = useState(null);
  const startXRef = useRef(0);
  const startWidthsRef = useRef({});

  const clampSidebar = useCallback((v) => Math.max(MIN_SIDEBAR, Math.min(500, v)), []);
  const clampInbox = useCallback((v) => Math.max(MIN_INBOX, Math.min(600, v)), []);

  useEffect(() => {
    if (resizing === null) return;

    const handleMove = (e) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const containerWidth = rect.width;
      const x = e.clientX - rect.left;

      if (resizing === "sidebar") {
        const maxSidebar = containerWidth - MIN_INBOX - MIN_VIEWER;
        const newSidebar = clampSidebar(Math.min(x, maxSidebar));
        setWidths((prev) => ({ ...prev, sidebar: newSidebar }));
      } else if (resizing === "inbox") {
        const sidebar = startWidthsRef.current.sidebar;
        const maxInbox = containerWidth - sidebar - MIN_VIEWER;
        const newInbox = clampInbox(Math.min(x - sidebar, maxInbox));
        setWidths((prev) => ({ ...prev, inbox: Math.max(MIN_INBOX, newInbox) }));
      }
    };

    const handleEnd = () => {
      setWidths((w) => {
        saveWidths(w);
        return w;
      });
      setResizing(null);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
    };
  }, [resizing, clampSidebar, clampInbox]);

  const startResize = (which) => (e) => {
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthsRef.current = { ...widths };
    setResizing(which);
  };

  return (
    <div
      ref={containerRef}
      className={`flex h-[calc(100vh-80px)] bg-white shadow-sm border border-gray-200 overflow-hidden
        ${resizing ? "cursor-col-resize" : ""}`}
    >
      <div
        className="relative flex-shrink-0 border-r border-gray-200 overflow-y-auto bg-gray-50 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        style={{ width: widths.sidebar, minWidth: MIN_SIDEBAR }}
      >
        {sidebar}
        <Resizer position="right" onDragStart={startResize("sidebar")} />
      </div>

      <div
        className="relative flex-shrink-0 border-r border-gray-200 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        style={{ width: widths.inbox, minWidth: MIN_INBOX }}
      >
        {inbox}
        <Resizer position="right" onDragStart={startResize("inbox")} />
      </div>

      <div
        className="flex-1 overflow-y-auto min-w-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
        style={{ minWidth: MIN_VIEWER }}
      >
        {viewer}
      </div>
    </div>
  );
};

export default MailLayout;
